/* ============================================
   BA111OD Watch Details – Hotspot Interactions
   ============================================ */
(function () {
  "use strict";

  var MOBILE_BREAKPOINT = 600;
  var DEBOUNCE_DELAY = 150;
  var TOOLTIP_GAP = 14;
  var ENTRANCE_STAGGER = 120;

  function init() {
    var sections = document.querySelectorAll("[data-wd-section]");
    sections.forEach(function (section) {
      new WatchDetails(section);
    });

    // Staggered entrance animation via IntersectionObserver
    if ("IntersectionObserver" in window) {
      var observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              entry.target.classList.add("wd-section--visible");
              observer.unobserve(entry.target);
            }
          });
        },
        { threshold: 0.15 }
      );

      sections.forEach(function (section) {
        var hotspots = section.querySelectorAll(".wd-hotspot");
        hotspots.forEach(function (hotspot, i) {
          hotspot.style.setProperty(
            "--wd-enter-delay",
            i * ENTRANCE_STAGGER + "ms"
          );
        });
        observer.observe(section);
      });
    } else {
      // Fallback: show all immediately
      sections.forEach(function (section) {
        section.classList.add("wd-section--visible");
      });
    }
  }

  function WatchDetails(root) {
    this.root = root;
    this.container = root.querySelector("[data-wd-container]");
    this.activeHotspot = null;
    this._onResize = debounce(this._handleResize.bind(this), DEBOUNCE_DELAY);

    this._bindEvents();
  }

  WatchDetails.prototype._bindEvents = function () {
    // Event delegation on root
    this.root.addEventListener("click", this._handleClick.bind(this));
    document.addEventListener("keydown", this._handleKeydown.bind(this));
    window.addEventListener("resize", this._onResize);
  };

  WatchDetails.prototype._handleClick = function (e) {
    var btn = e.target.closest(".wd-hotspot__btn");
    var closeBtn = e.target.closest("[data-wd-close]");

    if (closeBtn) {
      // Close button inside tooltip
      e.preventDefault();
      this._closeActive(true);
      return;
    }

    if (btn) {
      // Hotspot button click
      e.preventDefault();
      var hotspot = btn.closest(".wd-hotspot");
      if (hotspot === this.activeHotspot) {
        this._closeActive(true);
      } else {
        this._openHotspot(hotspot);
      }
      return;
    }

    // Click outside — close if not inside tooltip
    if (this.activeHotspot && !e.target.closest(".wd-tooltip")) {
      this._closeActive(false);
    }
  };

  WatchDetails.prototype._handleKeydown = function (e) {
    if (e.key === "Escape" && this.activeHotspot) {
      this._closeActive(true);
    }
  };

  WatchDetails.prototype._openHotspot = function (hotspot) {
    // Close any currently open hotspot
    if (this.activeHotspot) {
      this._closeActive(false);
    }

    this.activeHotspot = hotspot;

    var btn = hotspot.querySelector(".wd-hotspot__btn");
    var tooltip = hotspot.querySelector(".wd-tooltip");

    hotspot.classList.add("wd-hotspot--active");
    btn.setAttribute("aria-expanded", "true");
    tooltip.setAttribute("aria-hidden", "false");

    this._resetTooltipPosition(tooltip);

    if (this._isMobile()) {
      this.root.classList.add("wd-section--overlay");
      this._lockTooltipToViewport(hotspot);
    } else {
      this._positionTooltip(hotspot);
    }
  };

  WatchDetails.prototype._closeActive = function (returnFocus) {
    if (!this.activeHotspot) return;

    var hotspot = this.activeHotspot;
    var btn = hotspot.querySelector(".wd-hotspot__btn");
    var tooltip = hotspot.querySelector(".wd-tooltip");

    hotspot.classList.remove("wd-hotspot--active");
    btn.setAttribute("aria-expanded", "false");
    tooltip.setAttribute("aria-hidden", "true");

    this._resetTooltipPosition(tooltip);

    this.root.classList.remove("wd-section--overlay");
    this.activeHotspot = null;

    if (returnFocus) {
      btn.focus();
    }
  };

  WatchDetails.prototype._positionTooltip = function (hotspot) {
    var tooltip = hotspot.querySelector(".wd-tooltip");

    this._resetTooltipPosition(tooltip);

    var hotspotRect = hotspot.getBoundingClientRect();
    var viewportHeight = window.innerHeight;
    var viewportWidth = window.innerWidth;
    var tooltipWidth = tooltip.offsetWidth || 280;
    var tooltipHeight = tooltip.scrollHeight || 200;

    // Vertical: prefer below, use above if not enough viewport space
    var spaceBelow = viewportHeight - hotspotRect.bottom;
    var spaceAbove = hotspotRect.top;

    if (spaceBelow >= tooltipHeight + TOOLTIP_GAP) {
      tooltip.classList.add("wd-tooltip--below");
    } else if (spaceAbove >= tooltipHeight + TOOLTIP_GAP) {
      tooltip.classList.add("wd-tooltip--above");
    } else {
      // Pick whichever side has more room
      tooltip.classList.add(spaceBelow >= spaceAbove ? "wd-tooltip--below" : "wd-tooltip--above");
    }

    // Horizontal: prefer right-aligned, use left if not enough viewport space
    var spaceRight = viewportWidth - hotspotRect.left;
    var spaceLeft = hotspotRect.right;

    if (spaceRight >= tooltipWidth) {
      tooltip.classList.add("wd-tooltip--right");
    } else if (spaceLeft >= tooltipWidth) {
      tooltip.classList.add("wd-tooltip--left");
    } else {
      // Center if neither side has enough room
      tooltip.classList.add("wd-tooltip--right");
      tooltip.style.left = "calc(50% - " + (tooltipWidth / 2) + "px)";
    }
  };

  WatchDetails.prototype._handleResize = function () {
    if (this.activeHotspot) {
      if (this._isMobile()) {
        this.root.classList.add("wd-section--overlay");
        this._lockTooltipToViewport(this.activeHotspot);
      } else {
        this.root.classList.remove("wd-section--overlay");
        this._positionTooltip(this.activeHotspot);
      }
    }
  };

  WatchDetails.prototype._resetTooltipPosition = function (tooltip) {
    tooltip.classList.remove(
      "wd-tooltip--above",
      "wd-tooltip--below",
      "wd-tooltip--left",
      "wd-tooltip--right"
    );
    tooltip.style.left = "";
    tooltip.style.right = "";
    tooltip.style.top = "";
    tooltip.style.bottom = "";
    tooltip.style.width = "";
    tooltip.style.height = "";
  };

  WatchDetails.prototype._lockTooltipToViewport = function (hotspot) {
    var tooltip = hotspot.querySelector(".wd-tooltip");
    var rect = tooltip.getBoundingClientRect();
    var viewportWidth = document.documentElement.clientWidth || window.innerWidth;
    var viewportHeight = document.documentElement.clientHeight || window.innerHeight;

    tooltip.style.left = -rect.left + "px";
    tooltip.style.top = -rect.top + "px";
    tooltip.style.right = "auto";
    tooltip.style.bottom = "auto";
    tooltip.style.width = viewportWidth + "px";
    tooltip.style.height = viewportHeight + "px";
  };

  WatchDetails.prototype._isMobile = function () {
    return window.innerWidth <= MOBILE_BREAKPOINT;
  };

  function debounce(fn, delay) {
    var timer;
    return function () {
      var context = this;
      var args = arguments;
      clearTimeout(timer);
      timer = setTimeout(function () {
        fn.apply(context, args);
      }, delay);
    };
  }

  // Initialize on DOM ready
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
